import React, { useState } from "react"
import * as Icons from "lucide-react"
import { SPAM_SEED } from "../utils/spam"
import EmailFolderContextMenu from "./EmailFolderContextMenu"

const SYSTEM_ICONS = {
  inbox: Icons.Inbox,
  starred: Icons.Star,
  snoozed: Icons.Clock,
  sent: Icons.Send,
  drafts: Icons.FileText,
  archive: Icons.Archive,
  spam: Icons.ShieldAlert,
  junk: Icons.AlertTriangle,
  trash: Icons.Trash2,
}

const SPAM_UNREAD = SPAM_SEED.filter(m => !m.is_read).length

export default function EmailSidebar({
  accounts,
  activeAccount,
  onSelectAccount,
  folders,
  activeFolder,
  onSelectFolder,
  stats,
  labels,
  onOpenCompose,
  onOpenSettings,
  onAddMailbox,
  onSync,
  activeView,
  onChangeView,
  onCollapse,
  onFolderContextMenu,
  onFolderContextAction,
}) {
  const [collapsedFolders, setCollapsedFolders] = useState(new Set())
  const [expandedMore, setExpandedMore] = useState(false)
  const [collapsedSections, setCollapsedSections] = useState(new Set())
  const [folderMenu, setFolderMenu] = useState(null)

  const toggleSection = name => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }
  const favCollapsed = collapsedSections.has("fav")
  const acctCollapsed = collapsedSections.has("acct")

  const toggleCollapse = name => {
    setCollapsedFolders(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const getIcon = (folder, size = 15) => {
    const key = folder.system_folder
    const IconComp = key && SYSTEM_ICONS[key] ? SYSTEM_ICONS[key] : Icons.Folder
    return <IconComp size={size} />
  }

  const getCount = folder => {
    const s = folder.system_folder
    if (s === "inbox") return stats.unread
    if (s === "drafts") return stats.drafts
    if (s === "junk") return stats.spam
    if (s === "spam") return SPAM_UNREAD
    if (s === "trash") return stats.deleted
    if (s === "starred") return stats.starred
    if (s === "sent") return stats.sent
    if (folder.unread_count !== undefined) return folder.unread_count
    return null
  }

  const isSys = f =>
    [
      "inbox",
      "starred",
      "snoozed",
      "sent",
      "drafts",
      "archive",
      "spam",
      "junk",
      "trash",
    ].includes(f.system_folder)

  const mainFolders = folders.filter(f => isSys(f))
  const customFolders = folders.filter(f => !isSys(f))

  const renderFolder = (folder, depth = 0) => {
    const IconComp = getIcon(folder)
    const count = getCount(folder)
    const isActive =
      activeFolder === folder.name ||
      (folder.system_folder === "starred" && activeFolder === "starred")
    const key = folder.name || folder.system_folder

    return (
      <div
        key={key}
        className={`em-sidebar-folder ${
          isActive ? "em-sidebar-folder-active" : ""
        }`}
        style={depth > 0 ? { paddingLeft: 10 + depth * 16 } : undefined}
        onClick={() =>
          onSelectFolder(
            folder.system_folder === "starred" ? "starred" : folder.name
          )
        }
        onContextMenu={event => {
          event.preventDefault()
          event.stopPropagation()
          setFolderMenu({ x: event.clientX, y: event.clientY, folder })
          onFolderContextMenu?.(event, folder)
        }}
      >
        <span className="em-sidebar-folder-icon">{IconComp}</span>
        <span className="em-sidebar-folder-label">
          {folder.label || folder.name}
        </span>
        {count !== null && count !== undefined && count > 0 && (
          <span
            className={`em-sidebar-folder-count ${
              folder.system_folder === "inbox"
                ? "em-sidebar-folder-count-primary"
                : ""
            }`}
          >
            {folder.system_folder === "sent"
              ? count
              : count > 99
              ? "99+"
              : count}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className="em-sidebar">
      <div className="em-sidebar-newmail-row">
        <button className="em-sidebar-newmail" onClick={onOpenCompose}>
          <Icons.Pencil size={16} />
          <span>New mail</span>
        </button>
        {onCollapse && (
          <button
            className="em-sidebar-collapse-btn"
            onClick={onCollapse}
            title="Collapse folder pane"
            type="button"
          >
            <Icons.ChevronsLeft size={16} />
          </button>
        )}
      </div>

      <div className="em-sidebar-search-btn" onClick={onSync}>
        <Icons.RefreshCw size={14} />
        <span>Sync mailbox</span>
      </div>

      <div className="em-sidebar-search-btn" onClick={onOpenSettings}>
        <Icons.Settings size={14} />
        <span>Mailbox settings</span>
      </div>

      <div className="em-sidebar-search-btn" onClick={onAddMailbox}>
        <Icons.PlusCircle size={14} />
        <span>Add another mailbox</span>
      </div>

      <div className="em-sidebar-section">
        <div
          className="em-sidebar-section-title em-sidebar-section-title-toggle"
          onClick={() => toggleSection("fav")}
          title={favCollapsed ? "Expand" : "Collapse"}
        >
          <Icons.ChevronDown
            size={12}
            className="em-sidebar-section-chevron"
            style={{
              transform: favCollapsed ? "rotate(-90deg)" : "",
              transition: "transform 0.15s",
            }}
          />
          <span>Favourites</span>
        </div>
        {!favCollapsed && (
          <>
            <div
              className={`em-sidebar-folder ${
                activeFolder === "inbox" ? "em-sidebar-folder-active" : ""
              }`}
              onClick={() => onSelectFolder("inbox")}
              onContextMenu={event => {
                event.preventDefault()
                event.stopPropagation()
                const inbox = folders.find(f => f.system_folder === "inbox") || {
                  name: "inbox",
                  label: "Inbox",
                  system_folder: "inbox",
                }
                setFolderMenu({ x: event.clientX, y: event.clientY, folder: inbox })
                onFolderContextMenu?.(event, inbox)
              }}
            >
              <span className="em-sidebar-folder-icon">
                <Icons.Inbox size={15} />
              </span>
              <span className="em-sidebar-folder-label">Inbox</span>
              {stats.unread > 0 && (
                <span className="em-sidebar-folder-count em-sidebar-folder-count-primary">
                  {stats.unread > 99 ? "99+" : stats.unread}
                </span>
              )}
            </div>
            {mainFolders
              .filter(f => f.system_folder === "starred")
              .map(f => renderFolder(f))}
          </>
        )}
      </div>

      <div className="em-sidebar-section">
        <div
          className="em-sidebar-section-title em-sidebar-account-title em-sidebar-section-title-toggle"
          onClick={() => toggleSection("acct")}
          title={acctCollapsed ? "Expand" : "Collapse"}
        >
          <Icons.ChevronDown
            size={12}
            className="em-sidebar-section-chevron"
            style={{
              transform: acctCollapsed ? "rotate(-90deg)" : "",
              transition: "transform 0.15s",
            }}
          />
          <span title={activeAccount?.email_address}>
            {activeAccount?.email_address || "Folders"}
          </span>
        </div>
        {!acctCollapsed && (
          <>
            {mainFolders
              .filter(
                f =>
                  f.system_folder !== "starred" && f.system_folder !== "inbox"
              )
              .slice(0, expandedMore ? mainFolders.length : 5)
              .map(f => renderFolder(f))}
            {mainFolders.filter(
              f => f.system_folder !== "starred" && f.system_folder !== "inbox"
            ).length > 5 && (
              <div
                className="em-sidebar-more-btn"
                onClick={() => setExpandedMore(!expandedMore)}
              >
                <Icons.ChevronDown
                  size={12}
                  style={{
                    transform: expandedMore ? "rotate(180deg)" : "",
                    transition: "transform 0.15s",
                  }}
                />
                <span>{expandedMore ? "Less" : "More"}</span>
              </div>
            )}
          </>
        )}
      </div>

      {customFolders.length > 0 && (
        <div className="em-sidebar-section">
          <div className="em-sidebar-section-title">
            <span>Custom Folders</span>
          </div>
          {customFolders.map(f => renderFolder(f))}
        </div>
      )}

      {accounts.length > 1 && (
        <div className="em-sidebar-section">
          <div className="em-sidebar-section-title">
            <span>Accounts</span>
          </div>
          {accounts.map(a => (
            <div
              key={a.account_id}
              className={`em-sidebar-folder ${
                activeAccount?.account_id === a.account_id
                  ? "em-sidebar-folder-active"
                  : ""
              }`}
              style={{ paddingLeft: 16 }}
              onClick={() => onSelectAccount(a)}
            >
              <span className="em-sidebar-folder-icon">
                <Icons.User size={15} />
              </span>
              <span
                className="em-sidebar-folder-label"
                style={{ fontSize: 12 }}
              >
                {a.email_address}
              </span>
            </div>
          ))}
        </div>
      )}

      {labels.length > 0 && (
        <div className="em-sidebar-section">
          <div className="em-sidebar-section-title">
            <span>Labels</span>
          </div>
          <div className="em-sidebar-labels">
            {labels.map(l => (
              <div key={l.label_id} className="em-sidebar-label-item">
                <span
                  className="em-sidebar-label-dot"
                  style={{ background: l.color || "#2563eb" }}
                />
                <span className="em-sidebar-folder-label">{l.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {folderMenu && (
        <EmailFolderContextMenu
          x={folderMenu.x}
          y={folderMenu.y}
          folder={folderMenu.folder}
          canManage={!isSys(folderMenu.folder)}
          onClose={() => setFolderMenu(null)}
          onAction={(action, folder, confirm) =>
            onFolderContextAction?.(action, folder, confirm)
          }
        />
      )}
    </div>
  )
}
